import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageSquare, Send, Plus, Loader2 } from 'lucide-react';

const API = window.location.origin;
const STARTERS = [
  "How much is outstanding on invoices right now?",
  "What's in the jobs pipeline at the moment?",
  "How much money came in this month?",
  "Which quotes are still waiting on the customer?",
];

export const AIBusinessChat = ({ authHeaders }) => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('biz-chat-session') || '');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/api/admin/ai/chat/${sessionId}/messages`, authHeaders)
      .then((res) => setMessages(res.data?.messages || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const newChat = () => {
    setSessionId('');
    localStorage.removeItem('biz-chat-session');
    setMessages([]);
  };

  const send = async (text) => {
    const message = (text || input || '').trim();
    if (!message || streaming) return;
    setInput('');
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);

    const appendDelta = (delta) => {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + delta };
        return next;
      });
    };

    try {
      const res = await fetch(`${API}/api/admin/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith('data: ')) continue;
          const payload = chunk.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.session_id && obj.session_id !== sessionId) {
              setSessionId(obj.session_id);
              localStorage.setItem('biz-chat-session', obj.session_id);
            }
            if (obj.delta) appendDelta(obj.delta);
            if (obj.error) toast.error(obj.error);
          } catch { /* partial chunk, ignored */ }
        }
      }
    } catch (e) {
      toast.error(e.message || 'Chat failed — please try again');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Card data-testid="business-chat-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            Business Chat
            <span className="text-xs font-normal text-muted-foreground">GPT-5.5 · knows your live pipeline, quotes & invoices</span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={newChat} data-testid="business-chat-new">
            <Plus className="h-4 w-4 mr-1" /> New chat
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg bg-muted/30 h-80 overflow-y-auto p-3 space-y-3" data-testid="business-chat-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <p className="text-sm text-muted-foreground">Ask anything about your business — try one of these:</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="text-xs px-2.5 py-1 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                    data-testid={`business-chat-starter-${STARTERS.indexOf(s)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-background border'
              }`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : '')}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="flex gap-2 mt-3">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="e.g. Who still owes me money?"
            rows={1}
            className="resize-none"
            data-testid="business-chat-input"
          />
          <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="business-chat-send">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIBusinessChat;
